import { OrderType } from "@app/constants";
import clsx from "clsx";
import {
  useState,
  ChangeEvent,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import Checkbox from "../checkbox";
import { Images } from "@assets/images";
import { CustomSelect } from "../select/custom-select";
import ChevronDownArrow from "../select/chevron-down";
import IconLeftArrow from "../select/icon-left-arrow";
import IconRightArrow from "../select/icon-right-arrow";
import EditIcon from "@app/icons/edit";
import DeleteIcon from "@app/icons/delete";
import { Meta } from "@app/types";
import { generatePageList } from "@app/utils/util";
import RestoreIcon from "@app/icons/restore";

export interface Order {
  order: string;
  orderBy: string;
}

export interface OptionsOnClick {
  type?: number;
}

export interface TableConfig<D = any> {
  columnConfig: ColumnConfig[];

  hasCheckBox?: boolean;

  dataTable: D[];

  onRowClick?: (data: D) => void;

  onRowClickOrder?: (data?: Order) => void;

  onItemSelected?: (isSelectedAll: boolean, selectedItems: D[]) => void;

  /**
   * @default true
   * @summary
   * use useArrowTable for on/off arrow showing in the end of table row
   */
  useArrowTable?: boolean;

  componentEmpty?: ReactNode;

  hasFooter?: boolean;

  emptyWithHeader?: boolean;

  /**
   * Default: ''
   */
  tableClassName?: string;

  hasAction?: boolean;
  hasAction1?: boolean;
  hasAction2?: boolean;
  hasAction3?: boolean;

  meta?: Meta;

  onEditClick?: (item: any) => void;

  onDeleteClick?: (itemId: number) => void;

  onRestoreClick?: (itemId: number) => void;

  onDeletePermanentlyClick?: (itemId: number) => void;

  onPageChange?: (page: number) => void;

  onPageSizeChange?: (pageSize: number) => void;

  customProps?: {
    onClick?: ({
      data,
      options,
    }: {
      data?: any;
      options?: OptionsOnClick;
    }) => void;
  };
}

export interface ColumnConfig {
  label: string;

  dataKey: string;
  /**
   * width can be '%' or 'px,rem,em...' - if width is '%', table cannot have horizontal scroll (horizontal scroll is shown only if some of them is 'px')
   */
  width: string;

  headerClassName?: string;

  theadClassName?: string;

  tdClassName?: string;

  component?: any;
  /**
   * @default false
   * @description
   * If we don't pass this value, it will have `undefined` value. So that default value of it is false
   */
  hiddenAble?: boolean;

  hasOrder?: boolean;
}

/**
 * @summary
 * if u want table has horizontal scroll, u must set at least 2 `width` of `TableConfig` are 'px'
 */
function Table({
  columnConfig,
  hasCheckBox,
  dataTable,
  useArrowTable = false,
  emptyWithHeader = false,
  componentEmpty,
  tableClassName = "",
  hasAction = false,
  hasAction1 = false,
  hasAction2 = false,
  hasAction3 = false,
  meta,
  customProps,
  onItemSelected = () => {},
  onRowClick = () => {},
  onRowClickOrder = () => {},
  onEditClick = () => {},
  onDeleteClick = () => {},
  onDeletePermanentlyClick = () => {},
  onRestoreClick = () => {},
  onPageChange = () => {},
  onPageSizeChange = () => {},
}: TableConfig<any>) {
  const [selectedRow, setSelectedRow] = useState<any[]>([]);
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("");

  useEffect(() => {
    setIsSelectedAll(false);
    setSelectedRow([]);
  }, [dataTable]);

  const onClickCheckAllItems = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked: boolean = e.target.checked;

    setIsSelectedAll(isChecked);
    setSelectedRow([]);
    onItemSelected(isChecked, []);
  };

  const handleRowSelected = (e: ChangeEvent<HTMLInputElement>, item: any) => {
    let listSelected = [];

    if (e.target.checked) {
      listSelected = [...selectedRow, item];
      setSelectedRow(listSelected);
    } else {
      listSelected = isSelectedAll
        ? dataTable.filter((it) => it.id !== item.id)
        : selectedRow.filter((it) => it.id !== item.id);
      setSelectedRow(listSelected);
    }

    if (listSelected.length === dataTable.length) {
      setIsSelectedAll(true);
      onItemSelected(true, listSelected);

      return;
    }

    setIsSelectedAll(false);
    onItemSelected(false, listSelected);
  };

  const isRowChecked = (id: string) => {
    return isSelectedAll || selectedRow.filter((it) => it.id === id).length > 0;
  };

  const handleClickHeader = (item: any) => {
    if (item.hasOrder) {
      const orderBy = item.dataKey;
      const orderType =
        order === OrderType.ASC ? OrderType.DESC : OrderType.ASC;

      setOrderBy(orderBy);
      setOrder(orderType);
      onRowClickOrder({ order: orderType, orderBy });
    }
  };

  const handleEditClick = (it: any) => {
    onEditClick && onEditClick(it);
  };

  const handleDeleteClick = (itemId: number) => {
    onDeleteClick && onDeleteClick(itemId);
  };

  const handleDeletePermanentlyClick = (itemId: number) => {
    onDeletePermanentlyClick && onDeletePermanentlyClick(itemId);
  };

  const handleRestoreClick = (itemId: number) => {
    onRestoreClick && onRestoreClick(itemId);
  };

  const handlePageChange = (page: number) => {
    onPageChange && onPageChange(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    onPageSizeChange && onPageSizeChange(pageSize);
  };

  const handlePreviousPageClick = (page: number) => {
    onPageChange && onPageChange(page);
  };

  const handleNextPageClick = (page: number) => {
    onPageChange && onPageChange(page);
  };

  const calculateRange = useCallback(() => {
    const start = meta ? (meta.pageNum - 1) * meta.pageSize + 1 : 0;
    const end = meta ? Math.min(meta.pageNum * meta.pageSize, meta.total) : 0;
    const total = meta ? meta.total : 0;

    return `${start}-${end} of ${total} items`;
  }, [meta]);

  return dataTable.length || emptyWithHeader ? (
    <div>
      <div className="overflow-x-auto overflow-y-hidden">
        <table
          className={`table-auto border-b border-solid border-[#E1E2E9] ${tableClassName}`}
        >
          <thead>
            <tr className="border-t border-b border-solid border-[#E1E2E9] black-90 h-[52px] text-sm">
              {hasCheckBox && (
                <th>
                  <label className="inline-flex items-center">
                    <Checkbox
                      onChange={onClickCheckAllItems}
                      checked={isSelectedAll}
                      label=""
                      name="checkAll"
                      isMultiLanguage={false}
                    />
                  </label>
                </th>
              )}

              {columnConfig?.map((it) => (
                <th
                  key={it.dataKey}
                  className={clsx(
                    `text-left p-2 font-normal ${it.theadClassName}`,
                    {
                      "md_max:hidden": it.hiddenAble,
                    },
                    {
                      "bg-[#bac5d4]": it.dataKey === orderBy,
                    },
                    {
                      "cursor-pointer": it.hasOrder,
                    }
                  )}
                  style={{ width: it.width }}
                  onClick={() => handleClickHeader(it)}
                >
                  <div className={`flex items-center ${it.headerClassName}`}>
                    {it.label}

                    {it.hasOrder && (!order || it.dataKey !== orderBy) && (
                      <span className="ml-2">
                        <img
                          src={Images.SortIcon.default}
                          alt="sort-icon"
                          className="w-4"
                        />
                      </span>
                    )}

                    {it.hasOrder &&
                      order === OrderType.ASC &&
                      orderBy === it.dataKey && (
                        <span className="ml-2">
                          <img
                            src={Images.ArrowUpIcon.default}
                            alt="arrow-up-icon"
                            className="w-4"
                          />
                        </span>
                      )}

                    {it.hasOrder &&
                      order === OrderType.DESC &&
                      orderBy === it.dataKey && (
                        <span className="ml-2">
                          <img
                            src={Images.ArrowDownIcon.default}
                            alt="arrow-down-icon"
                            className="w-4"
                          />
                        </span>
                      )}
                  </div>
                </th>
              ))}

              {hasAction && (
                <th className="text-center p-2 font-normal">Action</th>
              )}

              {useArrowTable && <th></th>}
            </tr>
          </thead>

          <tbody>
            {dataTable?.map((it, idx: number) => (
              <tr
                key={idx}
                className="hover:bg-[#f7f8ff] text-sm text-[#6E7079]"
                onClick={() => onRowClick(it)}
              >
                {hasCheckBox && (
                  <td
                    className="p-2 align-middle"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="mt-2 px-auto text-center">
                      <label className="inline-flex items-center">
                        <Checkbox
                          onChange={(e) => handleRowSelected(e, it)}
                          checked={isRowChecked(it.id)}
                          label=""
                          name="checkRow"
                          isMultiLanguage={false}
                          checkboxClassName="w-4 h-4 cursor-pointer"
                        />
                      </label>
                    </div>
                  </td>
                )}

                {columnConfig?.map((col) => (
                  <td
                    key={col.dataKey}
                    className={clsx(`p-2 ${col.tdClassName}`, {
                      "md_max:hidden": col.hiddenAble,
                    })}
                  >
                    {col.component ? (
                      <col.component
                        data={it}
                        onClick={({ options }: { options?: OptionsOnClick }) =>
                          customProps?.onClick &&
                          customProps.onClick({ data: it, options })
                        }
                      />
                    ) : (
                      it[col.dataKey]
                    )}
                  </td>
                ))}

                {hasAction && (
                  <td className="p-2">
                    <div className="flex justify-center gap-2">
                      <div
                        className="cursor-pointer"
                        onClick={() => handleEditClick(it)}
                      >
                        <EditIcon />
                      </div>
                      <div
                        className="cursor-pointer"
                        onClick={() => handleDeleteClick(it.id)}
                      >
                        <DeleteIcon />
                      </div>
                    </div>
                  </td>
                )}

                {hasAction1 && (
                  <td className="p-2">
                    <div className="flex justify-center gap-2">
                      <div
                        className="cursor-pointer"
                        onClick={() => handleDeletePermanentlyClick(it.id)}
                      >
                        <DeleteIcon />
                      </div>
                      <div
                        className="cursor-pointer"
                        onClick={() => handleRestoreClick(it.id)}
                      >
                        <RestoreIcon />
                      </div>
                    </div>
                  </td>
                )}

                {hasAction2 && (
                  <td className="p-2">
                    <div className="flex justify-center gap-2">
                      <div
                        className="cursor-pointer"
                        onClick={() => handleEditClick(it)}
                      >
                        <EditIcon />
                      </div>
                    </div>
                  </td>
                )}

                {hasAction3 && (
                  <td className="p-2">
                    <div className="flex justify-center gap-2">
                      <div
                        className="cursor-pointer"
                        onClick={() => handleDeleteClick(it.id)}
                      >
                        <DeleteIcon />
                      </div>
                    </div>
                  </td>
                )}

                {useArrowTable && (
                  <td className="p-2">
                    <div className="w-4 h-4">
                      <img
                        src={Images.SpriteIcon.default}
                        alt="icon-left"
                        width={16}
                        height={16}
                      />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex justify-between items-center">
        <div className="flex justify-between items-center">
          <div className="mr-[10px]">
            <CustomSelect
              options={[
                { label: "20", value: 20 },
                { label: "50", value: 50 },
                { label: "100", value: 100 },
              ]}
              className="w-[60px] !h-[23px] bg-[rgba(94,99,102,0.08)] !border-none px-1 rounded-lg"
              optionContainerClassName="top-[23px] w-[60px]"
              optionClassName="!min-h-[23px] text-xs text-black whitespace-nowrap px-3"
              labelSelectedClassName="black-50 text-xs"
              arrow={<ChevronDownArrow />}
              defaultValue={20}
              hasBgColorHover
              onChange={(pageSize) => handlePageSizeChange(Number(pageSize))}
            />
          </div>
          <div className="black-20 text-sm mr-[22px]">Items per page</div>
          <div className="text-[#666] text-sm">{calculateRange()}</div>
        </div>
        <div className="flex justify-between items-center">
          <div className="mr-2">
            <CustomSelect
              options={generatePageList(meta?.totalPage)}
              className="w-[52px] !h-[23px] bg-[rgba(94,99,102,0.08)] !border-none px-1 rounded-lg"
              optionContainerClassName="top-[23px] w-[60px]"
              optionClassName="!min-h-[23px] text-xs text-black whitespace-nowrap px-3"
              labelSelectedClassName="black-50 text-xs"
              arrow={<ChevronDownArrow />}
              defaultValue={meta?.pageNum ?? 1}
              hasBgColorHover
              onChange={(page) => handlePageChange(Number(page))}
            />
          </div>
          <div className="black-20 text-sm mr-6 text-[#666]">
            of {meta?.totalPage ?? 0} pages
          </div>
          <div className="flex gap-2">
            <div
              className={clsx("cursor-pointer hover:bg-gray-200", {
                "cursor-not-allowed pointer-events-none":
                  meta && meta.pageNum === 1,
              })}
              onClick={() => handlePreviousPageClick(Number(meta?.pageNum) - 1)}
            >
              <IconLeftArrow />
            </div>
            <div
              className={clsx("cursor-pointer hover:bg-gray-200", {
                "cursor-not-allowed pointer-events-none":
                  meta && meta.pageNum === meta.totalPage,
              })}
              onClick={() => handleNextPageClick(Number(meta?.pageNum) + 1)}
            >
              <IconRightArrow />
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <>{componentEmpty}</>
  );
}

export default Table;
